/**
 * LPaaS 授权门禁 —— 方案 §5.6 / 对接文档 §4.2-①②、§5.1、§11 A-2
 *
 * PRD「运行态授权失败处理」的可执行化：**未授权不得进发布弹窗**（G0），
 * 且 🔴 任何时候都不许把「没授权」渲染成「发布失败」。
 *
 * 一期没有 `/auth/status` 端点 → 扫码完成只能靠 `/auth/check` 轮询（3s 一次、上限 120s）。
 * 超时或用户关闭引导 = phase 回 idle，🔴 不开弹窗、不产生任何批次记录。
 *
 * 🔴 分工：`startAuthorize()` 只负责「把扫码链接取回来」这一步，立即返回；
 *    扫没扫上是异步的，靠 `phase` 变化 + `onAuthorized/onTimeout` 回调通知页面。
 *    页面不要 `await startAuthorize()` 等用户扫码 —— 那样弹窗会一直转圈。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { POLL, ERR, AUTH_PHASE, TEXT, isSuccessCode } from '../constants';
import { toTimeMs } from '../utils';
import {
  checkAuth,
  applyAuthorize,
  applyManualToken,
} from '@/services/lpaasPublish';

/**
 * 阶段：idle → checking → needAuth → authorizing → ok / timeout
 * 🔴 枚举本体已移到 `constants.js`（组件层禁止 import hook/services，引导弹窗只吃 phase 字符串）。
 *    这里 re-export 只为兼容已有单测与页面 `import useLpaasAuth, { AUTH_PHASE }`。
 */
export { AUTH_PHASE };

/** 距到期不足 3 天 → 弹窗顶部黄条提醒（只提醒，不拦） */
const EXPIRING_SOON_DAYS = 3;

/** 时间解析统一走 utils.toTimeMs（兼容格式化串 / epoch 毫秒 / 纯数字串，两页同一套口径） */
const daysUntil = (expireAt) => {
  const ts = toTimeMs(expireAt);
  if (ts === null) return null;
  return (ts - Date.now()) / 86400000;
};

/**
 * @param {Object}   [handlers]
 * @param {Function} [handlers.onAuthorized] (data) => void 扫上码就回调（页面在这里开弹窗）
 * @param {Function} [handlers.onTimeout]    () => void 120s 没扫上
 * @param {Function} [handlers.onError]      ({code,msg}) => void 授权查询的网络/系统失败
 *    （≠ 未授权：phase 回 idle、引导不弹，页面在这里 toast 并收尾 pending 态）
 */
export default function useLpaasAuth(handlers = {}) {
  const [state, setState] = useState({
    phase: AUTH_PHASE.IDLE,
    authorizeUrl: '',
    qrText: '',
    expireAt: null,
    publisherName: '',
    expiringSoon: false,
    // needAuth 的原因分流：首次授权 / 已过期续扫（文案不同，引导按钮不同）
    reason: '',
    // 🔴 【临时方案】TEMPORARY-2026-10 手动token：是否显示「手动粘贴凭证」入口
    //    由后端 /auth/check 下发（Apollo 开关 + 非生产 profile）—— 前端不自己判环境
    manualTokenEntryEnabled: false,
    /** 凭证来源：SCAN / MANUAL（页面据此标「临时授权」） */
    authSource: '',
    /** 手动提交中的 loading 态（防连点） */
    manualSubmitting: false,
    /** 手动提交的失败原因（⚠️ 只放文案，**绝不放用户粘贴的凭证**） */
    manualError: '',
  });

  const timer = useRef(null);
  const deadline = useRef(0);
  const busy = useRef(false); // 🔴 复查请求没回来时不叠加下一次（网慢会并发堆请求）
  const mounted = useRef(true);
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  const clearPoll = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
    busy.current = false;
  };

  const applyCheck = (d) => {
    const left = daysUntil(d.expireAt);
    setState((s) => ({
      ...s,
      phase: AUTH_PHASE.OK,
      expireAt: d.expireAt ?? null,
      publisherName: d.publisherName || s.publisherName,
      // 🔴 只提醒不拦：还有 <3 天到期就提示提前续扫
      expiringSoon: left !== null && left >= 0 && left < EXPIRING_SOON_DAYS,
      // 🆕 临时方案：成功后清掉手动入口的提交态/错误（错误不许残留到下一屏）
      manualSubmitting: false,
      manualError: '',
      authSource: d.authSource ?? s.authSource,
    }));
  };

  /**
   * 把后端下发的「入口是否显示」同步到 state（🔴 任何一次拿到 data 都要同步，
   * 否则会出现「未授权时入口不见了、扫码完又冒出来」这种看起来像 bug 的抖动）。
   * ⚠️ services.mapAuthState 已把「字段未下发」归一为 false（见 services 单测契约：
   *    未下发 = 入口不显示），所以这里不需要再留「不覆盖现有值」的分支。
   */
  const syncManualFlag = (d) => !!d.manualTokenEntryEnabled;

  /**
   * ① 点【新增发布】的第一件事：查授权。
   * @return {Promise<boolean>} true = 已授权，调用方可以继续拉 /app/list
   */
  const ensureAuthorized = useCallback(async () => {
    setState((s) => ({ ...s, phase: AUTH_PHASE.CHECKING }));
    const res = await checkAuth();
    if (!mounted.current) return false;

    const d = (res && res.data) || {};
    const manualFlag = syncManualFlag(d);

    // 🔴 非授权类失败（网络断 / 系统错误等）≠ 未授权：不能渲染成「首次请扫码」去误导
    //    用户扫一场扫不上的码。phase 回 idle（引导不弹），失败交给 onError 收尾。
    if (
      !res ||
      (!isSuccessCode(res.code) &&
        res.code !== ERR.AUTH_INVALID &&
        res.code !== ERR.AUTH_IN_PROGRESS)
    ) {
      setState((s) => ({ ...s, phase: AUTH_PHASE.IDLE }));
      if (latest.current.onError) {
        latest.current.onError({
          code: res && res.code,
          msg: (res && res.msg) || TEXT.AUTH_CHECK_FAIL,
        });
      }
      return false;
    }

    // 07020050 授权无效 / 07020051 授权进行中 → 一律走扫码闭环，不许当普通失败 toast
    if (res.code === ERR.AUTH_INVALID || res.code === ERR.AUTH_IN_PROGRESS) {
      setState((s) => ({
        ...s,
        phase: AUTH_PHASE.NEED_AUTH,
        reason: 'invalid',
        expireAt: null,
        // 🔴 报错分支也要同步开关：否则“授权无效”时用户连手动入口都看不到，只能卡住
        manualTokenEntryEnabled: manualFlag,
      }));
      return false;
    }

    if (d.authorized) {
      applyCheck(d);
      setState((s) => ({ ...s, manualTokenEntryEnabled: manualFlag }));
      return true;
    }
    setState((s) => ({
      ...s,
      phase: AUTH_PHASE.NEED_AUTH,
      expireAt: d.expireAt ?? null,
      publisherName: d.publisherName || '',
      // expireAt 为 null = 从未授权（首次）；已过期 = 续扫 → 文案不一样
      reason: d.expireAt ? 'expired' : 'firstTime',
      manualTokenEntryEnabled: manualFlag,
    }));
    return false;
  }, []);

  /**
   * ② 用户点【向用户申请授权】：取扫码链接并起 3s 复查（上限 120s）。
   * @return {Promise<{ok:boolean, authorizeUrl?:string, qrText?:string, code?:string, msg?:string}>}
   *         ok 只代表「链接拿到了」，不代表用户已扫码
   */
  const startAuthorize = useCallback(async () => {
    clearPoll();
    setState((s) => ({ ...s, phase: AUTH_PHASE.AUTHORIZING }));
    const res = await applyAuthorize();
    if (!mounted.current) return { ok: false };
    if (!res || !isSuccessCode(res.code)) {
      setState((s) => ({ ...s, phase: AUTH_PHASE.NEED_AUTH }));
      return {
        ok: false,
        code: res && res.code,
        msg: (res && res.msg) || '获取授权链接失败',
      };
    }

    const d = res.data || {};
    setState((s) => ({
      ...s,
      authorizeUrl: d.authorizeUrl || '',
      qrText: d.qrText || '',
    }));

    deadline.current = Date.now() + POLL.AUTH_MAX_MS;
    clearPoll();
    timer.current = setInterval(async () => {
      if (busy.current) return;
      if (Date.now() > deadline.current) {
        clearPoll();
        if (mounted.current) {
          setState((s) => ({ ...s, phase: AUTH_PHASE.TIMEOUT }));
          if (latest.current.onTimeout) latest.current.onTimeout();
        }
        return;
      }
      busy.current = true;
      let check = null;
      try {
        check = await checkAuth();
      } finally {
        busy.current = false;
      }
      if (!mounted.current) return;
      const d2 = (check && check.data) || {};
      if (!d2.authorized) return; // 还没扫，等下一拍
      clearPoll();
      applyCheck(d2);
      if (latest.current.onAuthorized) latest.current.onAuthorized(d2);
    }, POLL.AUTH_MS);

    return {
      ok: true,
      authorizeUrl: d.authorizeUrl || '',
      qrText: d.qrText || '',
      expireIn: d.expireIn,
    };
  }, []);

  /**
   * 🔴 【临时方案】TEMPORARY-2026-10 手动token：提交用户粘贴的 LPaaS 凭证。
   *
   * <p>行为：成功 → 走**现有的 {@link applyCheck}**（phase→ok、页面开弹窗）；
   * 失败 → 按错误码给明确文案，🔴 <b>绝不把失败当成「还没扫」</b>——
   * 那正是本模块已知缺陷 D1 的老毛病（授权链路任何异常，用户看到的都是「请扫码」，白等 120 秒）。</p>
   *
   * <p>⚠️ 本方法<b>不记录、不回显、不上报</b>入参内容：错误文案里也不允许拼凭证（哪怕片段）。</p>
   *
   * @param {string} token 用户粘贴的凭证原文（只在内存里传到请求体，不落任何本地存储）
   * @return {Promise<{ok:boolean, code?:string, msg?:string}>}
   */
  const submitManualToken = useCallback(async (token) => {
    const value = String(token == null ? '' : token).trim();
    if (!value) {
      // 🔴 空值也必须给可见反馈：不能静默早返回。
      //    （2026-10-03 现场事故的第二个成因：组件因 ref 读到 undefined 交了空串，
      //     这里又默默 return，两处叠起来就是“点了没反应”。任一处都不该有这个可能。）
      setState((s) => ({
        ...s,
        manualSubmitting: false,
        manualError: TEXT.AUTH_GUIDE_MANUAL_EMPTY,
      }));
      return { ok: false, msg: TEXT.AUTH_GUIDE_MANUAL_EMPTY };
    }
    setState((s) => ({ ...s, manualSubmitting: true, manualError: '' }));
    const res = await applyManualToken({ token: value });
    if (!mounted.current) return { ok: false };

    if (!res || !isSuccessCode(res.code)) {
      const code = res && res.code;
      // 🔴 2026-10-03 修复：后端 msg 优先。手动链路的失败原因只有后端知道（探活失败原文 /
      //    命名空间字典解析不出来 / 该环境看不到应用），前端一句固定文案覆写掉它，
      //    会把「字典解析 bug」说成「你贴的凭证是 31 位、是扫码通道的」——排查方向直接被带偏。
      //    固定文案降级为「后端没给文案时的兜底」。
      let msg = (res && res.msg) || '';
      if (!msg) {
        msg =
          code === ERR.AUTH_INVALID || code === ERR.LPAAS_APP_NOT_FOUND
            ? TEXT.AUTH_GUIDE_MANUAL_INVALID
            : TEXT.AUTH_GUIDE_MANUAL_FAIL;
      }
      if (code === ERR.FEATURE_DISABLED) {
        // 入口没开是「环境/开关」问题，不是凭证问题，后端文案只说入口未开启，这里补上出路
        msg = TEXT.AUTH_GUIDE_MANUAL_DISABLED;
      }
      setState((s) => ({
        ...s,
        manualSubmitting: false,
        manualError: msg,
        // 🔴 失败**不改变 phase**：失败不是「已授权」，也不该被当成「还没扫码」
        //    （引导弹窗仍然停在 needAuth，用户可以直接重贴）
        phase: AUTH_PHASE.NEED_AUTH,
      }));
      return { ok: false, code, msg };
    }

    const d = res.data || {};
    clearPoll();
    applyCheck(d);
    if (latest.current.onAuthorized) latest.current.onAuthorized(d);
    return { ok: true };
  }, []);

  /** 用户关掉引导 / 授权流程作废：回到 idle（🔴 不开弹窗、不产生任何记录） */
  const reset = useCallback(() => {
    clearPoll();
    setState((s) => ({
      ...s,
      phase: AUTH_PHASE.IDLE,
      authorizeUrl: '',
      qrText: '',
      expiringSoon: false,
      reason: '',
      // 清理手动入口的瞬时态（凭证本身不在 state 里，无需清理）
      manualSubmitting: false,
      manualError: '',
    }));
  }, []);

  return {
    phase: state.phase,
    authorizeUrl: state.authorizeUrl,
    qrText: state.qrText,
    expireAt: state.expireAt,
    publisherName: state.publisherName,
    expiringSoon: state.expiringSoon,
    reason: state.reason,
    manualTokenEntryEnabled: state.manualTokenEntryEnabled,
    authSource: state.authSource,
    manualSubmitting: state.manualSubmitting,
    manualError: state.manualError,
    ensureAuthorized,
    startAuthorize,
    submitManualToken,
    reset,
  };
}
